/**
 * Agents list hook — thin TanStack Query wrapper around agentsApi.list.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { agentsApi, type CreateAgentPayload } from '@/api/agents';

export function useAgents(filter: 'all' | 'active' | 'failed' = 'all') {
  return useQuery({
    queryKey: ['agents', 'list', filter],
    queryFn: () => agentsApi.list({ filter }),
    staleTime: 5_000,
  });
}

export function useCreateAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateAgentPayload) => agentsApi.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agents'] });
    },
  });
}

export function useDeleteAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, stepupToken }: { name: string; stepupToken: string }) =>
      agentsApi.remove(name, stepupToken),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agents'] });
    },
  });
}

export function useAgentAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, action }: { name: string; action: 'start' | 'stop' | 'restart' }) =>
      agentsApi.action(name, action),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['agents'] });
      qc.invalidateQueries({ queryKey: ['agent', variables.name] });
    },
  });
}
